// src/pages/Admin/Adherents/CreateEditObjectifModal.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaBullseye,
  FaCalendarAlt,
  FaCheck,
  FaCheckCircle,
  FaClipboardList,
  FaFlagCheckered,
  FaLayerGroup,
  FaPercent,
  FaSave,
  FaSpinner,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";

/* ---------- Helpers ---------- */
const seasonLabel = (d = new Date()) => {
  const y = d.getFullYear();
  const m = d.getMonth();
  const start = m >= 8 ? y : y - 1;
  return `${start}-${start + 1}`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const TYPES = [
  { value: "loisir", label: "Loisir" },
  { value: "ceinture", label: "Ceinture" },
  { value: "poids", label: "Poids" },
  { value: "renforcement", label: "Renforcement" },
  { value: "competition", label: "Compétition" },
  { value: "kata", label: "Kata" },
  { value: "uv2", label: "UV2" },
  { value: "uv3_points", label: "UV3 - Points" },
  { value: "autre", label: "Autre" },
];

const QUICK_PROGRESS = [0, 25, 50, 75, 100];

const inputClass =
  "min-h-[46px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-violet-400 dark:focus:ring-violet-950/60";

const labelClass =
  "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400";

const getTypeLabel = (value) =>
  TYPES.find((item) => item.value === value)?.label || "Non défini";

const SectionCard = ({ icon: Icon, title, description, children }) => (
  <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/80">
    <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white shadow-sm">
          <Icon className="text-sm" />
        </div>

        <div>
          <h4 className="text-sm font-bold text-slate-900 dark:text-white">
            {title}
          </h4>
          {description && (
            <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              {description}
            </p>
          )}
        </div>
      </div>
    </div>

    <div className="p-4">{children}</div>
  </section>
);

export default function CreateEditObjectifModal({
  open,
  onClose,
  onSubmit,
  initial,
  adherentId,
}) {
  const isEdit = !!initial?.id;
  const dialogRef = useRef(null);

  const [title, setTitle] = useState("");
  const [type, setType] = useState("");
  const [saison, setSaison] = useState(seasonLabel());
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [progress, setProgress] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    setTitle(initial?.title || "");
    setType(initial?.type || "");
    setSaison(initial?.saison || seasonLabel());
    setDescription(initial?.description || "");
    setStartDate(initial?.start_date?.slice?.(0, 10) || "");
    setDueDate(initial?.due_date?.slice?.(0, 10) || "");
    setProgress(typeof initial?.progress === "number" ? initial.progress : 0);
    setIsDone(!!initial?.is_done);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;

    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };

    window.addEventListener("keydown", onKey);

    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='title']")?.focus();
    }, 0);

    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      root.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const progressNumber = Number(progress) || 0;

  const canSubmit = useMemo(() => {
    return !!adherentId && title.trim().length > 0 && !submitting;
  }, [adherentId, title, submitting]);

  const dateWarning = useMemo(() => {
    if (!startDate || !dueDate) return "";
    if (new Date(dueDate) < new Date(startDate)) {
      return "L’échéance est antérieure à la date de début.";
    }
    return "";
  }, [startDate, dueDate]);

  const summaryText = useMemo(() => {
    if (isDone || progressNumber >= 100) return "Objectif terminé";
    if (progressNumber >= 75) return "Objectif presque atteint";
    if (progressNumber >= 50) return "Objectif bien engagé";
    if (progressNumber > 0) return "Objectif démarré";
    return "Objectif à démarrer";
  }, [isDone, progressNumber]);

  const onProgressChange = (v) => {
    const n = Math.max(0, Math.min(100, Number(v) || 0));
    setProgress(n);

    if (n >= 100) {
      setIsDone(true);
    } else if (isDone) {
      setIsDone(false);
    }
  };

  const onDoneToggle = (checked) => {
    setIsDone(checked);

    if (checked && progressNumber < 100) {
      setProgress(100);
    }

    if (!checked && progressNumber === 100) {
      setProgress(99);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    if (!canSubmit) {
      if (!title.trim()) toast.error("Le titre est requis.");
      if (!adherentId) toast.error("Adhérent introuvable.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        adherent_id: adherentId,
        title: title.trim(),
        type: type || null,
        saison: saison || null,
        description: description || null,
        start_date: startDate || todayISO(),
        due_date: dueDate || todayISO(),
        progress: Number(progress) || 0,
        is_done: !!isDone,
      };

      await onSubmit(payload, initial?.id);
      handleClose();
    } catch (err) {
      console.error("Erreur handleSubmit Objectif:", err);
      toast.error("Échec de l’enregistrement de l’objectif.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? "Modifier un objectif" : "Créer un objectif"}
    >
      <div
        ref={dialogRef}
        className="relative flex max-h-[94dvh] w-full max-w-5xl flex-col overflow-hidden rounded-t-[2rem] bg-slate-50 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 sm:max-h-[90dvh] sm:rounded-[2rem]"
        style={{ WebkitOverflowScrolling: "touch" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative overflow-hidden bg-gradient-to-r from-sky-500 via-violet-600 to-orange-400 px-4 py-5 sm:px-6">
          <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 left-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/75">
                Objectif adhérent
              </p>

              <h3 className="mt-1 text-xl font-bold text-white sm:text-2xl">
                {isEdit ? "Modifier l’objectif" : "Créer un objectif"}
              </h3>

              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-white/85">
                Définissez un objectif sportif, une échéance et un niveau de
                progression pour suivre l’évolution de l’adhérent.
              </p>
            </div>

            <button
              onClick={handleClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25 disabled:opacity-60"
              aria-label="Fermer"
              type="button"
              title="Fermer"
              disabled={submitting}
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="objectif-modal-scroll min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
              {/* Colonne synthèse */}
              <aside className="space-y-5">
                <section className="overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-sm dark:border-violet-950 dark:bg-slate-900">
                  <div className="bg-gradient-to-br from-violet-600 to-sky-500 p-5 text-white">
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
                        <FaBullseye className="text-xl" />
                      </div>

                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-white/70">
                          Avancement
                        </p>
                        <p className="text-lg font-bold">{summaryText}</p>
                      </div>
                    </div>

                    <div className="mt-5">
                      <div className="mb-2 flex items-center justify-between text-xs font-bold text-white/80">
                        <span>Progression</span>
                        <span>{progressNumber}%</span>
                      </div>

                      <div className="h-3 overflow-hidden rounded-full bg-white/20">
                        <div
                          className="h-full rounded-full bg-white transition-all"
                          style={{ width: `${progressNumber}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 p-4">
                    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Type
                      </p>
                      <p className="mt-1 text-sm font-bold text-slate-800 dark:text-white">
                        {getTypeLabel(type)}
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Saison
                      </p>
                      <p className="mt-1 text-sm font-bold text-slate-800 dark:text-white">
                        {saison || seasonLabel()}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => onDoneToggle(!isDone)}
                      className={`flex min-h-[48px] w-full items-center justify-center gap-2 rounded-2xl px-4 py-2 text-sm font-bold transition ${
                        isDone
                          ? "bg-emerald-600 text-white shadow-sm hover:bg-emerald-700"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                      }`}
                    >
                      {isDone ? <FaCheckCircle /> : <FaCheck />}
                      {isDone ? "Objectif terminé" : "Marquer terminé"}
                    </button>
                  </div>
                </section>

                <SectionCard
                  icon={FaPercent}
                  title="Progression rapide"
                  description="Boutons pratiques sur smartphone."
                >
                  <div className="grid grid-cols-5 gap-2">
                    {QUICK_PROGRESS.map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => onProgressChange(value)}
                        className={`min-h-[44px] rounded-xl text-sm font-bold transition ${
                          progressNumber === value
                            ? "bg-violet-600 text-white shadow-sm"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                        }`}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </SectionCard>
              </aside>

              {/* Formulaire principal */}
              <main className="space-y-5">
                <SectionCard
                  icon={FaClipboardList}
                  title="Informations"
                  description="Le titre est obligatoire. Les autres champs permettent de mieux classer l’objectif."
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className={labelClass}>
                        Titre <span className="text-pink-600">*</span>
                      </label>
                      <input
                        name="title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Ex. Obtenir la ceinture verte"
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Type</label>
                      <select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        className={inputClass}
                      >
                        <option value="">— Sélectionner —</option>
                        {TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Saison</label>
                      <input
                        value={saison}
                        onChange={(e) => setSaison(e.target.value)}
                        placeholder="2025-2026"
                        className={inputClass}
                      />
                      <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                        Défaut :{" "}
                        <span className="font-bold">{seasonLabel()}</span>
                      </p>
                    </div>

                    <div className="sm:col-span-2">
                      <label className={labelClass}>Description</label>
                      <textarea
                        rows={4}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Détails, étapes, remarques de l’enseignant…"
                        className={`${inputClass} resize-none leading-relaxed`}
                      />
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaCalendarAlt}
                  title="Planification"
                  description="Les dates sont optionnelles. Si elles sont vides, la date du jour sera utilisée."
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelClass}>Date de début</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Échéance</label>
                      <input
                        type="date"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>

                    {dateWarning && (
                      <div className="sm:col-span-2 rounded-2xl bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-700 ring-1 ring-orange-100 dark:bg-orange-950/30 dark:text-orange-300 dark:ring-orange-900">
                        {dateWarning}
                      </div>
                    )}
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaFlagCheckered}
                  title="Suivi de l’objectif"
                  description="Ajustez la progression ou cochez directement l’objectif comme terminé."
                >
                  <div className="space-y-5">
                    <div>
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <label className={labelClass}>Progression</label>

                        <div className="rounded-xl bg-violet-50 px-3 py-1 text-sm font-bold text-violet-700 ring-1 ring-violet-100 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-900">
                          {progressNumber}%
                        </div>
                      </div>

                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={progressNumber}
                        onChange={(e) => onProgressChange(e.target.value)}
                        className="w-full accent-violet-600"
                      />

                      <div className="mt-2 flex justify-between text-[11px] font-semibold text-slate-400">
                        <span>0%</span>
                        <span>50%</span>
                        <span>100%</span>
                      </div>
                    </div>

                    <label
                      htmlFor="doneChk"
                      className={`flex cursor-pointer items-center justify-between gap-4 rounded-2xl p-4 ring-1 transition ${
                        isDone
                          ? "bg-emerald-50 ring-emerald-200 dark:bg-emerald-950/30 dark:ring-emerald-900"
                          : "bg-slate-50 ring-slate-200 hover:bg-slate-100 dark:bg-slate-950 dark:ring-slate-800 dark:hover:bg-slate-900"
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <span
                          className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                            isDone
                              ? "bg-emerald-600 text-white"
                              : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                          }`}
                        >
                          <FaCheckCircle />
                        </span>

                        <span>
                          <span className="block text-sm font-bold text-slate-800 dark:text-white">
                            Objectif terminé
                          </span>
                          <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                            Si coché, la progression passe automatiquement à
                            100%.
                          </span>
                        </span>
                      </span>

                      <input
                        id="doneChk"
                        type="checkbox"
                        className="h-6 w-6 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        checked={isDone}
                        onChange={(e) => onDoneToggle(e.target.checked)}
                      />
                    </label>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaLayerGroup}
                  title="Résumé"
                  description="Contrôle rapide avant enregistrement."
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Titre
                      </p>
                      <p className="mt-1 line-clamp-2 text-sm font-bold text-slate-800 dark:text-white">
                        {title.trim() || "—"}
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Type
                      </p>
                      <p className="mt-1 text-sm font-bold text-slate-800 dark:text-white">
                        {getTypeLabel(type)}
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        État
                      </p>
                      <p className="mt-1 text-sm font-bold text-slate-800 dark:text-white">
                        {summaryText}
                      </p>
                    </div>
                  </div>
                </SectionCard>
              </main>
            </div>
          </div>

          {/* Footer sticky */}
          <div className="sticky bottom-0 z-10 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                title="Annuler"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-600/20 transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
                title={isEdit ? "Enregistrer" : "Créer"}
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Enregistrement…
                  </>
                ) : (
                  <>
                    <FaSave />
                    {isEdit
                      ? "Enregistrer les modifications"
                      : "Créer l’objectif"}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .objectif-modal-scroll::-webkit-scrollbar {
          width: 10px;
        }

        .objectif-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.45);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }

        .objectif-modal-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .dark .objectif-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.35);
          border: 3px solid transparent;
          background-clip: content-box;
        }
      `}</style>
    </div>,
    document.body
  );
}